import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

// Resolve the Node API base URL (same pattern as the rest of the app).
const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  const { hostname, protocol } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'http://localhost:3000';
  return `${protocol}//${hostname}`;
};
const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const Account = () => {
  const { user, updateProfile, changePassword } = useAuth();
  const navigate = useNavigate();
  const plan = (user?.subscription || 'free');
  const planLabel = plan.charAt(0).toUpperCase() + plan.slice(1);
  const isPremium = plan.toLowerCase() === 'premium';

  // Derive first/last from the user object (profile returns first/last now; older
  // sessions only have `name` = "First Last").
  const nameParts = (user?.name || '').trim().split(/\s+/);
  const initialFirst = user?.firstName || nameParts[0] || '';
  const initialLast = user?.lastName || nameParts.slice(1).join(' ') || '';

  // Change-password form state (Security card). Only meaningful for local
  // (email+password) accounts; OAuth accounts have no password to change.
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const [pwdSaving, setPwdSaving] = useState(false);
  const isOAuth = user?.authProvider && user.authProvider !== 'local';

  const submitPassword = async (e) => {
    e.preventDefault();
    if (pwd.next.length < 8) { toast.error('New password must be at least 8 characters.'); return; }
    if (pwd.next !== pwd.confirm) { toast.error('New passwords do not match.'); return; }
    setPwdSaving(true);
    const result = await changePassword(pwd.current, pwd.next);
    setPwdSaving(false);
    if (result.success) setPwd({ current: '', next: '', confirm: '' });
  };

  // Name is editable ONLY when it was never properly set — i.e. empty or the
  // placeholder "Member" with no last name. This lets any user (incl. social
  // login) set their name ONCE if it is missing; once a real name exists it is
  // locked (read-only) for everyone, OAuth or local.
  const nameNeedsSetup =
    !initialLast ||
    initialFirst === '' ||
    initialFirst.toLowerCase() === 'member';

  const [firstName, setFirstName] = useState(initialFirst);
  const [lastName, setLastName] = useState(initialLast);
  const [saving, setSaving] = useState(false);

  // Watchlist alert email opt-in (per-member, default off). Loaded from the server.
  const [emailAlerts, setEmailAlerts] = useState(false);
  const [alertsSaving, setAlertsSaving] = useState(false);

  // Weekly AI-picks digest opt-in (separate from watchlist alerts).
  const [weeklyDigest, setWeeklyDigest] = useState(false);
  const [digestSaving, setDigestSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${getApiUrl()}/api/watchlist/prefs?cache=${Date.now()}`, { headers: authHeaders() });
        if (!res.ok) return;
        const json = await res.json();
        if (!cancelled) setEmailAlerts(!!json.emailAlertsOptIn);
      } catch (_) { /* non-fatal */ }
      try {
        const res = await fetch(`${getApiUrl()}/api/weekly-digest/prefs?cache=${Date.now()}`, { headers: authHeaders() });
        if (!res.ok) return;
        const json = await res.json();
        if (!cancelled) setWeeklyDigest(!!json.weeklyDigestOptIn);
      } catch (_) { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggleWeeklyDigest = async (next) => {
    setWeeklyDigest(next); // optimistic
    setDigestSaving(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/weekly-digest/prefs?weeklyDigestOptIn=${next}`, {
        method: 'PUT', headers: authHeaders(),
      });
      const json = await res.json();
      if (res.ok && json.status === 'ok') {
        toast.success(next ? 'Weekly digest enabled' : 'Weekly digest disabled');
      } else {
        setWeeklyDigest(!next);
        toast.error('Could not save preference');
      }
    } catch (_) {
      setWeeklyDigest(!next);
      toast.error('Could not save preference');
    } finally {
      setDigestSaving(false);
    }
  };

  const toggleEmailAlerts = async (next) => {
    setEmailAlerts(next); // optimistic
    setAlertsSaving(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/watchlist/prefs?emailAlertsOptIn=${next}`, {
        method: 'PUT', headers: authHeaders(),
      });
      const json = await res.json();
      if (res.ok && json.status === 'ok') {
        toast.success(next ? 'Watchlist alert emails enabled' : 'Watchlist alert emails disabled');
      } else {
        setEmailAlerts(!next); // revert
        toast.error('Could not save preference');
      }
    } catch (_) {
      setEmailAlerts(!next);
      toast.error('Could not save preference');
    } finally {
      setAlertsSaving(false);
    }
  };

  const handleSave = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      toast.error('Please enter both first and last name.');
      return;
    }
    setSaving(true);
    const result = await updateProfile({ firstName: firstName.trim(), lastName: lastName.trim() });
    setSaving(false);
    if (result?.success) toast.success('Profile updated.');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container-custom">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Account Settings</h1>
        
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold">Profile Information</h3>
              </div>
              <div className="card-body">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">First Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      readOnly={!nameNeedsSetup}
                    />
                  </div>
                  <div>
                    <label className="form-label">Last Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      readOnly={!nameNeedsSetup}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-input"
                      value={user?.email || ''}
                      readOnly
                    />
                  </div>
                </div>
                {nameNeedsSetup ? (
                  <div className="mt-6">
                    <button className="btn-primary" onClick={handleSave} disabled={saving}>
                      {saving ? 'Saving…' : 'Save Profile'}
                    </button>
                    <p className="text-xs text-gray-400 mt-2">
                      Set your name here. Email cannot be changed.
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 mt-6">
                    Your name and email are locked. Contact support to change them.
                  </p>
                )}
              </div>
            </div>

            {/* Notifications: per-member opt-in for watchlist alert emails. */}
            <div className="card mt-6">
              <div className="card-header">
                <h3 className="text-lg font-semibold">Notifications</h3>
              </div>
              <div className="card-body">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={emailAlerts}
                    disabled={alertsSaving}
                    onChange={(e) => toggleEmailAlerts(e.target.checked)}
                  />
                  <span>
                    <span className="block font-medium text-gray-900">Alerts for stocks in my watchlist</span>
                    <span className="block text-sm text-gray-500">
                      Email me when a new signal fires on a stock I've added to my watchlist.
                      Personal to the stocks you track. Unsubscribe anytime.
                    </span>
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer mt-4">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={weeklyDigest}
                    disabled={digestSaving}
                    onChange={(e) => toggleWeeklyDigest(e.target.checked)}
                  />
                  <span>
                    <span className="block font-medium text-gray-900">Weekly roundup of AI picks &amp; rankings</span>
                    <span className="block text-sm text-gray-500">
                      A once-a-week email with GandalStock's current AI picks (Top Pick, Breakout,
                      Value), the AI-Rank leaderboard, and any picks that recently closed —
                      across the whole app, not just your watchlist. Unsubscribe anytime.
                    </span>
                  </span>
                </label>
              </div>
            </div>
          </div>

          <div>
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold">Subscription</h3>
              </div>
              <div className="card-body">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600 capitalize mb-2">
                    {planLabel}
                  </div>
                  <div className="text-sm text-gray-500 mb-4">
                    {isPremium ? 'Active subscription' : 'Free plan'}
                  </div>
                  <button
                    onClick={() => navigate(isPremium ? '/billing' : '/pricing')}
                    className="btn-outline w-full"
                  >
                    {isPremium ? 'Manage Subscription' : 'Upgrade to Premium'}
                  </button>
                </div>
              </div>
            </div>

            {/* Security — change password (local accounts only) */}
            <div className="card mt-6">
              <div className="card-header">
                <h3 className="text-lg font-semibold">Security</h3>
              </div>
              <div className="card-body">
                {isOAuth ? (
                  <p className="text-sm text-gray-500">
                    You sign in with {user.authProvider}. Password changes are managed by your provider.
                  </p>
                ) : (
                  <form onSubmit={submitPassword} className="space-y-3">
                    <div>
                      <label className="form-label">Current password</label>
                      <input
                        type="password" autoComplete="current-password" required
                        className="form-input" value={pwd.current}
                        onChange={(e) => setPwd({ ...pwd, current: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="form-label">New password</label>
                      <input
                        type="password" autoComplete="new-password" required
                        className="form-input" placeholder="At least 8 characters" value={pwd.next}
                        onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="form-label">Confirm new password</label>
                      <input
                        type="password" autoComplete="new-password" required
                        className="form-input" value={pwd.confirm}
                        onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
                      />
                    </div>
                    <button type="submit" disabled={pwdSaving} className="btn-primary w-full">
                      {pwdSaving ? 'Updating…' : 'Update password'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Account;
